import { Injectable, inject, NgZone, OnDestroy, Component, Inject } from '@angular/core';
import { MatSnackBar, MAT_SNACK_BAR_DATA, MatSnackBarRef } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

import { Router } from '@angular/router';
import { LogOutModalComponent } from '../components/log-out-modal/log-out-modal.component';

@Component({
  selector: 'sjp-warning-snackbar',
  standalone: true,
  imports: [MatIconModule],
  template: `
    <div class="flex-row v-center jc-between" style="width: 100%; gap: 1rem;">
      <div class="flex-row v-center gap-05">
        <mat-icon style="color: inherit;">warning</mat-icon>
        <span style="font-weight: 500; color: var(--text-primary);">{{ data }}</span>
      </div>
      <button
        class="close-btn"
        aria-label="Close"
        i18n-aria-label="@@warning.snackbar.close"
        (click)="snackBarRef.dismissWithAction()"
      >
        <mat-icon class="grey-icon">close</mat-icon>
      </button>
    </div>
  `,
  styles: [
    `
      .close-btn {
        background: transparent;
        border: none;
        padding: 0;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--text-secondary) !important;
      }
      .grey-icon {
        color: var(--text-secondary) !important;
      }
      :host {
        display: block;
        background-color: var(--element-error-bg-color, #fce4e4);
        border: 1px solid var(--element-error-color, #d32f2f);
        color: var(--element-error-color, #d32f2f);
        padding: 8px 16px;
        border-radius: 8px;
      }
    `,
  ],
})
export class WarningSnackbarComponent {
  public data = inject<string>(MAT_SNACK_BAR_DATA);
  public snackBarRef = inject(MatSnackBarRef<WarningSnackbarComponent>);
}

@Injectable({
  providedIn: 'root',
})
export class InactivityService implements OnDestroy {
  private _ngZone = inject(NgZone);
  private _snackBar = inject(MatSnackBar);
  private _dialog = inject(MatDialog);
  private _authService = inject(AuthService);

  private _router = inject(Router);

  private _activityTimer: any;
  private _timeoutTimer: any;

  // Timers config from env (fallback to standard defaults if not provided)
  private get _warningMs(): number {
    return (environment.sessionWarningMinutes || 28) * 60 * 1000;
  }

  private get _timeoutMs(): number {
    return (environment.sessionTimeoutMinutes || 30) * 60 * 1000;
  }

  private _isTracking = false;
  private _dialogRef: any = null;
  private _warningRef: MatSnackBarRef<WarningSnackbarComponent> | null = null;

  // DOM events to monitor
  private _events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
  private _boundReset = (e: Event) => this._resetTimers(e);

  public startTracking(): void {
    if (this._isTracking) return;
    this._isTracking = true;

    // Run outside Angular to avoid triggering CD on every mouse move
    this._ngZone.runOutsideAngular(() => {
      this._events.forEach((event) => document.addEventListener(event, this._boundReset, true));
    });

    this._startTimers();
  }

  public stopTracking(): void {
    if (!this._isTracking) return;
    this._isTracking = false;

    this._events.forEach((event) => document.removeEventListener(event, this._boundReset, true));
    this._clearTimers();
    this._closeOverlays();
  }

  private _resetTimers(force: boolean | Event = false): void {
    if (force !== true && (this._dialogRef || this._warningRef)) {
      // If the blocking modal or warning is up, ignore background DOM events.
      // The user MUST explicitly interact with the prompt to reset.
      return;
    }

    this._warningRef = null;
    this._clearTimers();
    this._startTimers();

    // Only dismiss the inactivity warning snackbar. Other application snackbars
    // (e.g. validation or errors) should remain visible when the user moves the mouse.
    this._ngZone.run(() => {
      this._warningRef?.dismiss();
      this._warningRef = null;
    });
  }

  private _startTimers(): void {
    this._ngZone.runOutsideAngular(() => {
      this._activityTimer = setTimeout(() => {
        this._ngZone.run(() => this._showWarning());
      }, this._warningMs);

      this._timeoutTimer = setTimeout(() => {
        this._ngZone.run(() => this._triggerTimeout());
      }, this._timeoutMs);
    });
  }

  private _clearTimers(): void {
    clearTimeout(this._activityTimer);
    clearTimeout(this._timeoutTimer);
  }

  private _closeOverlays(): void {
    this._warningRef?.dismiss();
    this._warningRef = null;
    this._snackBar.dismiss();
    if (this._dialogRef) {
      this._dialogRef.close();
      this._dialogRef = null;
    }
  }

  private _showWarning(): void {
    if (!this._authService.isAuthenticated()) return;

    const remainingMinutes = Math.round((this._timeoutMs - this._warningMs) / 60000);
    const message =
      remainingMinutes === 1
        ? $localize`:@@warning.snackbar.message.singular:Warning: 1 minute to log out`
        : $localize`:@@warning.snackbar.message.plural:Warning: ${remainingMinutes}:remainingMinutes: minutes to log out`;

    this._warningRef = this._snackBar.openFromComponent(WarningSnackbarComponent, {
      data: message,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: 'warning-snackbar-panel',
    });

    this._warningRef.onAction().subscribe(() => {
      this._resetTimers(true);
    });

    this._warningRef.afterDismissed().subscribe(() => {
      this._warningRef = null;
    });
  }

  private _triggerTimeout(): void {
    if (!this._authService.isAuthenticated() || this._dialogRef) return;

    this._snackBar.dismiss();

    // Show blocking modal
    this._dialogRef = this._dialog.open(LogOutModalComponent, {
      disableClose: true,
      width: '480px',
      maxWidth: '90vw',
    });

    this._dialogRef.afterClosed().subscribe((confirmLogout: boolean) => {
      this._dialogRef = null;
      if (confirmLogout) {
        this.stopTracking();
        this._ngZone.run(() => {
          this._router.navigate(['/log-out']);
        });
      } else {
        this._resetTimers(); // Extend session
      }
    });
  }

  ngOnDestroy(): void {
    this.stopTracking();
  }
}
